import { useEffect, useState, useCallback } from "react";
import {
  Container, Typography, Box, Paper, Grid, TextField, Button, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, Chip, Stack, Divider,
  Alert, CircularProgress, Tabs, Tab,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import { ServerConfig } from "../../../connectors/Config";

/**
 * StrumentiConformita
 * -------------------------------------------------------------------
 * Strumenti operativi di conformità al Decreto D.Dir. 1986/2024, tutti
 * collegati alla Lambda serverless2-dev-decretoManage:
 *   - Validazione conformità corso        POST /decreto/validazioni/corso
 *   - Verifica propedeuticità             POST /decreto/validazioni/propedeuticita
 *   - Registro presenze (check-in/out)    POST /decreto/presenze/check-in|check-out
 *   - Documenti richiesti per tipologia   GET  /decreto/documenti-richiesti
 */

const API = ServerConfig.AWS.Serverless.API_BASE_URL_2;

const TabPanel = ({ value, index, children }) =>
  value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;

const StrumentiConformita = () => {
  const [tab, setTab] = useState(0);

  // --- Validazione corso ---
  const [scId, setScId] = useState("");
  const [valResult, setValResult] = useState(null);
  const [valLoading, setValLoading] = useState(false);
  const [valError, setValError] = useState(null);

  const validaCorso = async () => {
    setValLoading(true); setValError(null); setValResult(null);
    try {
      const r = await fetch(`${API}/decreto/validazioni/corso`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ school_course_id: scId.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Errore validazione");
      setValResult(d);
    } catch (e) { setValError(e.message); } finally { setValLoading(false); }
  };

  // --- Propedeuticità ---
  const [propCorso, setPropCorso] = useState("");
  const [propProfile, setPropProfile] = useState("");
  const [propResult, setPropResult] = useState(null);
  const [propLoading, setPropLoading] = useState(false);
  const [propError, setPropError] = useState(null);

  const verificaProp = async () => {
    setPropLoading(true); setPropError(null); setPropResult(null);
    try {
      const r = await fetch(`${API}/decreto/validazioni/propedeuticita`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ course_id: propCorso.trim(), partecipant_id: propProfile.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Errore verifica");
      setPropResult(d);
    } catch (e) { setPropError(e.message); } finally { setPropLoading(false); }
  };

  // --- Registro presenze ---
  const [presSc, setPresSc] = useState("");
  const [presPt, setPresPt] = useState("");
  const [presId, setPresId] = useState("");
  const [presMsg, setPresMsg] = useState(null);
  const [presErr, setPresErr] = useState(null);

  const checkIn = async () => {
    setPresErr(null); setPresMsg(null);
    try {
      const r = await fetch(`${API}/decreto/presenze/check-in`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ school_course_id: presSc.trim(), partecipant_id: presPt.trim(), tipo_lezione: "teoria" }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Errore check-in");
      setPresId(d.presenza.id);
      setPresMsg(`Check-in registrato (${d.presenza.data_lezione} ${d.presenza.ora_inizio}). ID presenza: ${d.presenza.id}`);
    } catch (e) { setPresErr(e.message); }
  };
  const checkOut = async () => {
    setPresErr(null); setPresMsg(null);
    try {
      const r = await fetch(`${API}/decreto/presenze/check-out`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ presenza_id: presId.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Errore check-out");
      setPresMsg(`Check-out registrato. Minuti di presenza: ${d.presenza.minuti_presenza}`);
    } catch (e) { setPresErr(e.message); }
  };

  // --- Documenti richiesti ---
  const [tipologie, setTipologie] = useState([]);
  const [tipSel, setTipSel] = useState("");
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    fetch(`${API}/decreto/tipologie`).then((r) => r.json())
      .then((d) => setTipologie(d.tipologie || [])).catch(() => {});
  }, []);

  const loadDocs = useCallback(async (tipId) => {
    setTipSel(tipId);
    if (!tipId) { setDocs([]); return; }
    const r = await fetch(`${API}/decreto/documenti-richiesti?tipologia_id=${tipId}`);
    const d = await r.json();
    setDocs(d.documenti || []);
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", color: "rgb(4,14,99)" }}>
          Strumenti Conformità Decreto
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Validazioni, registro presenze e documenti richiesti — D.Dir. 1986/2024
        </Typography>
      </Box>

      <Paper sx={{ borderRadius: 3 }} elevation={2}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
          <Tab icon={<FactCheckIcon />} iconPosition="start" label="Validazione corso" />
          <Tab icon={<FactCheckIcon />} iconPosition="start" label="Propedeuticità" />
          <Tab icon={<LoginIcon />} iconPosition="start" label="Registro presenze" />
          <Tab icon={<FactCheckIcon />} iconPosition="start" label="Documenti richiesti" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* --- TAB 0: validazione corso --- */}
          <TabPanel value={tab} index={0}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
              <TextField fullWidth size="small" label="ID edizione corso (school_course.id)"
                value={scId} onChange={(e) => setScId(e.target.value)} />
              <Button variant="contained" onClick={validaCorso} disabled={!scId || valLoading}
                sx={{ minWidth: 140 }}>{valLoading ? <CircularProgress size={22} /> : "Valida"}</Button>
            </Stack>
            {valError && <Alert severity="error" sx={{ mt: 2 }}>{valError}</Alert>}
            {valResult && (
              <Box sx={{ mt: 3 }}>
                <Alert severity={valResult.conforme ? "success" : "warning"} sx={{ mb: 2 }}>
                  Corso <strong>{valResult.corso}</strong> ({valResult.acronimo}):{" "}
                  {valResult.conforme ? "CONFORME al decreto" : "NON conforme — verifiche da correggere"}
                </Alert>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Regola</TableCell><TableCell>Atteso</TableCell>
                    <TableCell>Effettivo</TableCell><TableCell>Esito</TableCell><TableCell>Messaggio</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {valResult.checks.map((c, i) => (
                      <TableRow key={i}>
                        <TableCell>{c.regola}</TableCell>
                        <TableCell>{String(c.atteso)}</TableCell>
                        <TableCell>{String(c.effettivo)}</TableCell>
                        <TableCell>{c.esito
                          ? <CheckCircleIcon color="success" fontSize="small" />
                          : <CancelIcon color="error" fontSize="small" />}</TableCell>
                        <TableCell><Typography variant="caption">{c.messaggio}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </TabPanel>

          {/* --- TAB 1: propedeuticità --- */}
          <TabPanel value={tab} index={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}><TextField fullWidth size="small" label="ID corso (courses.id)"
                value={propCorso} onChange={(e) => setPropCorso(e.target.value)} /></Grid>
              <Grid item xs={12} sm={5}><TextField fullWidth size="small" label="ID discente (profiles.id)"
                value={propProfile} onChange={(e) => setPropProfile(e.target.value)} /></Grid>
              <Grid item xs={12} sm={2}><Button fullWidth variant="contained" onClick={verificaProp}
                disabled={!propCorso || !propProfile || propLoading} sx={{ height: 40 }}>
                {propLoading ? <CircularProgress size={22} /> : "Verifica"}</Button></Grid>
            </Grid>
            {propError && <Alert severity="error" sx={{ mt: 2 }}>{propError}</Alert>}
            {propResult && (
              <Box sx={{ mt: 3 }}>
                <Alert severity={propResult.ammissibile ? "success" : "error"}>
                  {propResult.ammissibile ? "Discente AMMISSIBILE — propedeuticità soddisfatte"
                    : "Discente NON ammissibile — corsi propedeutici mancanti"}
                </Alert>
                {propResult.mancanti?.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">Corsi propedeutici mancanti:</Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                      {propResult.mancanti.map((m) => (
                        <Chip key={m.corso_id} color="error" label={`${m.acronimo} — ${m.nome_corso}`} />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            )}
          </TabPanel>

          {/* --- TAB 2: registro presenze --- */}
          <TabPanel value={tab} index={2}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Registrazione presenze giornaliere (All. 15). Il check-out calcola automaticamente i minuti di presenza.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="ID edizione corso (school_course.id)"
                value={presSc} onChange={(e) => setPresSc(e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="ID iscrizione (school_course_partecipant.id)"
                value={presPt} onChange={(e) => setPresPt(e.target.value)} /></Grid>
            </Grid>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button variant="contained" startIcon={<LoginIcon />} onClick={checkIn}
                disabled={!presSc || !presPt}>Check-in</Button>
              <TextField size="small" label="ID presenza" value={presId}
                onChange={(e) => setPresId(e.target.value)} sx={{ flexGrow: 1 }} />
              <Button variant="outlined" startIcon={<LogoutIcon />} onClick={checkOut}
                disabled={!presId}>Check-out</Button>
            </Stack>
            {presErr && <Alert severity="error" sx={{ mt: 2 }}>{presErr}</Alert>}
            {presMsg && <Alert severity="success" sx={{ mt: 2 }}>{presMsg}</Alert>}
          </TabPanel>

          {/* --- TAB 3: documenti richiesti --- */}
          <TabPanel value={tab} index={3}>
            <TextField select fullWidth size="small" label="Tipologia discente" value={tipSel}
              onChange={(e) => loadDocs(e.target.value)} sx={{ maxWidth: 420 }}>
              <MenuItem value="">— seleziona —</MenuItem>
              {tipologie.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.codice} — {t.descrizione}</MenuItem>
              ))}
            </TextField>
            {docs.length > 0 && (
              <Table size="small" sx={{ mt: 2 }}>
                <TableHead><TableRow>
                  <TableCell>Documento</TableCell><TableCell>Obbligatorio</TableCell>
                  <TableCell>Formato</TableCell><TableCell>Validità (mesi)</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {docs.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{d.nome_documento}</Typography>
                        {d.descrizione && <Typography variant="caption" color="text.secondary">{d.descrizione}</Typography>}
                      </TableCell>
                      <TableCell>{d.obbligatorio
                        ? <Chip size="small" color="error" label="Obbligatorio" />
                        : <Chip size="small" label="Facoltativo" />}</TableCell>
                      <TableCell>{d.formato_accettato || "—"}</TableCell>
                      <TableCell>{d.validita_mesi || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabPanel>
        </Box>
      </Paper>
    </Container>
  );
};

export default StrumentiConformita;
